/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {PopoverModule} from 'ngx-bootstrap/popover';
import * as _ from 'lodash';

import template from './list-column-selector.component.html';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {UserProfileApiService} from '../../admin/user-profile/user-profile-api.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferViewWrite} from '../util/template-state';
import {AppSharedModule} from '../app-shared.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, PopoverModule],
  selector: 'adn-list-column-selector',
  template
})
export class ListColumnSelectorComponent implements OnInit, OnChanges {
  @Input() allFields: any[];
  @Input() defaultFields: any[];
  @Input() sel: any;
  @Input() changeHook: any;
  @Input() profileParam = '';
  @Input() view = '';

  private localUser: any;
  private initializationScheduled = false;

  constructor(
    private readonly adnLog: AdnLogService,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.localUser = this.localUserProfile.get();
    this.scheduleInitializeSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultFields || changes.allFields) {
      this.scheduleInitializeSelection();
    }
  }

  /**
   * The selected profile columns mutate the parent list. Defer that initial
   * write so the parent cannot observe defaults and profile fields in the
   * same change-detection verification pass.
   */
  private scheduleInitializeSelection(): void {
    if (this.initializationScheduled) {
      return;
    }
    this.initializationScheduled = true;
    deferViewWrite(this.cdr, () => {
      this.initializationScheduled = false;
      this.initializeSelection();
    });
  }

  private initializeSelection(): void {
    if (_.isString(this.sel)) {
      this.adnLog.warn('ListColumnSelector: expected object binding for sel, got string:', this.sel);
      this.sel = {};
    }
    this.sel = this.sel || {};

    if (!this.defaultFields?.length) {
      return;
    }

    if (_.isEmpty(this.sel.selFields) || _.isEmpty(this.sel.outputFields)) {
      this.sel.outputFields = _.cloneDeep(this.defaultFields);
      this.sel.selFields = _.map(this.defaultFields, (l: any) => this.fieldId(l));
    }

    if (this.view === 'SLIM') {
      return;
    }

    const profile = this.localUserProfile.get().profile;
    const profileSelFieldIds = _.get(profile[this.profileParam], 'selFieldIds') || [];
    if (profileSelFieldIds.length > 0 && this.allFields?.length) {
      const verifiedSelFields = _.filter(this.allFields, (l: any) => {
        return profileSelFieldIds.indexOf(this.fieldId(l)) > -1;
      });
      if (verifiedSelFields.length > 0) {
        this.sel.selFields = _.map(verifiedSelFields, (l: any) => this.fieldId(l));
        this.sel.outputFields = verifiedSelFields;
        this.fieldsUpdated();
      }
    }
  }

  fieldLabelKey(field: any): string {
    return 'label.' + (field.otherLabel || field.label || field.param || field);
  }

  isFieldSelected(field: any): boolean {
    return (this.sel.selFields || []).indexOf(this.fieldId(field)) > -1;
  }

  onFieldSelectionChange(field: any, checked: boolean): void {
    const fieldId = this.fieldId(field);
    const selected = new Set(this.sel.selFields || []);
    if (checked) {
      selected.add(fieldId);
    } else {
      selected.delete(fieldId);
    }
    this.sel.selFields = _.map(_.filter(this.allFields, (l: any) => selected.has(this.fieldId(l))), (l: any) => this.fieldId(l));
    this.updateFields();
  }

  private fieldId(field: any): string {
    return field.param || field.label || field;
  }

  private fieldsUpdated(): void {
    if (_.isFunction(_.get(this, ['changeHook', 'fieldChange']))) {
      this.changeHook.fieldChange();
    }
  }

  private updateFields(): void {
    this.sel.outputFields = _.filter(this.allFields, (l: any) => {
      return this.sel.selFields.indexOf(this.fieldId(l)) > -1;
    });
    this.fieldsUpdated();

    if (_.isString(this.profileParam) && this.profileParam) {
      const profile = this.localUser.profile;
      profile[this.profileParam] = {
        selFieldIds: this.sel.selFields
      };
      firstValueFrom(this.UserProfile.saveProfile({
        id: this.localUser.userId,
        profile: _.pick(profile, [this.profileParam])
      })).then((apiUser: any) => {
        this.localUser = this.localUserProfile.safeSaveProfile(this.profileParam, apiUser.profile[this.profileParam]);
      });
    } else {
      this.adnLog.warn("Can't save settings because of a faulty profileParam of " + this.profileParam);
    }
  }
}
